/**
 * FauxDS Tooltip
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate Tooltip component for prototyping.
 * It is NOT the real @faire/slate Tooltip and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate Tooltip, see: @faire/slate/components/tooltip
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { useState, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

export type TooltipPosition = "top" | "bottom" | "left" | "right";
export type TooltipAlignment = "start" | "middle" | "end";

export interface TooltipProps {
  /**
   * The tooltip trigger element
   */
  children: React.ReactNode;
  /**
   * Plain-text label (used when `content` is not provided)
   */
  label?: string;
  /**
   * Rich tooltip body; when set, rendered instead of `label`
   */
  content?: React.ReactNode;
  /**
   * Classes for the portaled tooltip panel (width, padding, colors). Defaults preserve the original string tooltip look.
   */
  tooltipClassName?: string;
  /**
   * The tooltip position relative to the trigger
   * @default "top"
   */
  position?: TooltipPosition;
  /**
   * The tooltip alignment relative to the trigger
   * @default "middle"
   */
  alignment?: TooltipAlignment;
  /**
   * **playground only**
   * Optional className for additional styling on the trigger wrapper
   */
  className?: string;
  /**
   * Render a directional caret arrow pointing from the panel toward the trigger.
   * Matches the Figma tooltip design. Defaults to false for backward compatibility.
   */
  showPointer?: boolean;
}

const defaultTooltipPanelClass =
  "pointer-events-none rounded-[var(--slate-radius-component-default)] bg-[var(--slate-color-surface-primary-inverse)] px-[12px] py-[8px] font-sans text-[12px] leading-[16px] text-[var(--slate-color-text-primary-inverse)]";

/**
 * Tooltip - FauxDS (recreated from Slate)
 *
 * Tooltips display informative text when users hover or focus on an element.
 * This is a simplified implementation for prototyping purposes.
 */
export default function Tooltip({
  children,
  label,
  content,
  tooltipClassName,
  position = "top",
  alignment = "middle",
  className = "",
  showPointer = false,
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const showTooltip = useCallback(() => setIsVisible(true), []);
  const hideTooltip = useCallback(() => setIsVisible(false), []);

  const panelClassName = tooltipClassName ?? defaultTooltipPanelClass;

  // Calculate position when tooltip becomes visible
  useEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const gap = 12;

      let top = 0;
      let left = 0;

      // Calculate position
      switch (position) {
        case "top":
          top = triggerRect.top - tooltipRect.height - gap;
          break;
        case "bottom":
          top = triggerRect.bottom + gap;
          break;
        case "left":
          left = triggerRect.left - tooltipRect.width - gap;
          top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
          break;
        case "right":
          left = triggerRect.right + gap;
          top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
          break;
      }

      // Calculate horizontal alignment for top/bottom positions
      if (position === "top" || position === "bottom") {
        switch (alignment) {
          case "start":
            left = triggerRect.left;
            break;
          case "middle":
            left =
              triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
            break;
          case "end":
            left = triggerRect.right - tooltipRect.width;
            break;
        }
      }

      // Calculate vertical alignment for left/right positions
      if (position === "left" || position === "right") {
        switch (alignment) {
          case "start":
            top = triggerRect.top;
            break;
          case "middle":
            top =
              triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
            break;
          case "end":
            top = triggerRect.bottom - tooltipRect.height;
            break;
        }
      }

      setTooltipPosition({ top, left });
    }
  }, [isVisible, position, alignment]);

  const body = content ?? label ?? "";

  return (
    <>
      {/* Trigger wrapper */}
      <div
        ref={triggerRef}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        className={cn("inline-flex items-center", className)}
      >
        {children}
      </div>

      {/* Tooltip content - rendered in portal */}
      {isVisible &&
        createPortal(
          <div
            className="fixed z-[700] animate-[fadeIn_0.15s_ease-out]"
            style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
          >
            <div
              ref={tooltipRef}
              role="tooltip"
              className={cn("shadow-lg", panelClassName)}
            >
              {body}
            </div>
            {showPointer && position === "top" && (
              <div className="absolute bottom-[-8px] left-1/2 h-0 w-0 -translate-x-1/2 border-x-[8px] border-t-[8px] border-x-transparent border-t-[#333]" />
            )}
            {showPointer && position === "right" && (
              <div className="absolute top-1/2 left-[-8px] h-0 w-0 -translate-y-1/2 border-y-[8px] border-r-[8px] border-y-transparent border-r-[#333]" />
            )}
            {showPointer && position === "left" && (
              <div className="absolute top-1/2 right-[-8px] h-0 w-0 -translate-y-1/2 border-y-[8px] border-l-[8px] border-y-transparent border-l-[#333]" />
            )}
          </div>,
          document.body
        )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </>
  );
}
